import { FC } from 'react';
import { Popover, Box, Typography } from '@mui/material';
import { Mail, Phone } from '@mui/icons-material';
import { components } from '@types';
import { t } from 'i18next';

import { theme } from '@store';
import { useHistoryUserPopover } from '../hooks/useHistoryUserPopover.ts';
import { ProfileAvatar } from '../../ProfileAvatar';
import {
  ClipboardButton,
  TruncatedTextWithConditionalTooltip,
} from 'components';

interface HistoryUserPopover {
  user: components['schemas']['UserVariablePayload'];
}

export const HistoryUserPopover: FC<HistoryUserPopover> = ({ user }) => {
  const { handleClose, handleOpen, anchorEl } = useHistoryUserPopover();
  return (
    <>
      <Typography
        variant='body2'
        component='span'
        sx={{
          color: 'primary.main',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={handleOpen}
      >
        {user.name}
      </Typography>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: 260,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ProfileAvatar
              size='medium'
              encoded_image={user.image?.thumbnail_xsmall}
              firstName={user.first_name}
              lastName={user.last_name}
              status={
                user.activity_status === 'active'
                  ? user.activity_status
                  : 'away'
              }
            />
            <Typography
              variant='subtitle2'
              sx={{
                maxWidth: '166px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {user.name}
            </Typography>
          </Box>
          <Typography
            sx={{
              padding: '4px 16px',
              backgroundColor: theme => theme.palette.orange.main,
              borderRadius: 4,
              color: theme.palette.common.white,
              mt: 2,
              mb: 1,
            }}
          >
            {t(`roles.${user.role}`)}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 0',
              alignItems: 'center',
            }}
          >
            <Mail sx={{ height: 16 }} color='primary' />
            <TruncatedTextWithConditionalTooltip
              width='150px'
              text={user.email}
              variant='body2'
            />
            <ClipboardButton textToCopy={user.email} size={16} />
          </Box>
          {user.phone_number && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                padding: '4px 0',
                alignItems: 'center',
              }}
            >
              <Phone sx={{ height: 16 }} color='primary' />
              <TruncatedTextWithConditionalTooltip
                width='150px'
                text={user.phone_number}
                variant='body2'
              />
              <ClipboardButton textToCopy={user.phone_number || ''} size={16} />
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};
